import React, { FC } from 'react';
import Box from '@mui/material/Box';
import {
  colourNeutral2,
  colourNeutral3,
  colourSemanticNegative,
  colourSemanticPositive,
  colourTextAndIcon1,
  colourTextAndIcon2,
} from '@ingka/variables';
import useTranslation from '../../hooks/useTranslation';
import { ForecastRow, formatKeur, formatSignedKeur } from './utils';

type ForecastTableProps = {
  rows: ForecastRow[];
  reportedLabel: string;
};

const diffColour = (value: number | null) => {
  if (!value) return undefined;
  return value < 0 ? colourSemanticNegative : colourSemanticPositive;
};

const ForecastTable: FC<ForecastTableProps> = ({ rows, reportedLabel }) => {
  const t = useTranslation();

  const rowLabel = (key: string) =>
    key === 'total'
      ? t('financialFrames.columns.total')
      : t(`financialFrames.columns.${key}`);

  return (
    <Box
      component="table"
      sx={{
        width: '100%',
        tableLayout: 'auto',
        borderCollapse: 'collapse',
        color: colourTextAndIcon1,
        'th, td': { px: 1.5 },
        th: {
          whiteSpace: 'normal',
          verticalAlign: 'bottom',
          fontWeight: 'normal',
          color: colourTextAndIcon2,
          textAlign: 'left',
          pb: 1,
        },
        td: {
          py: 2.5,
          textAlign: 'right',
          whiteSpace: 'nowrap',
          position: 'relative',
        },
        'td:first-of-type': { textAlign: 'left' },
        // Short inset dividers between the cells
        'td + td::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: '25%',
          bottom: '25%',
          borderLeft: `1px solid ${colourNeutral3}`,
        },
        'tbody tr:nth-of-type(odd)': { backgroundColor: colourNeutral2 },
      }}
    >
      <thead>
        <tr>
          <th />
          <th>{t('financialFrames.rmppTotal.allocatedFrame')}</th>
          <th>{reportedLabel}</th>
          <th>{t('financialFrames.rmppTotal.diff')}</th>
          <th>{t('financialFrames.rmppTotal.actuals')}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td>{rowLabel(row.key)}</td>
            <td>{formatKeur(row.allocated)}</td>
            <td>{formatKeur(row.reported)}</td>
            <td style={{ color: diffColour(row.diff) }}>
              {formatSignedKeur(row.diff)}
            </td>
            <td>{formatKeur(row.actuals)}</td>
          </tr>
        ))}
      </tbody>
    </Box>
  );
};

export default ForecastTable;
